import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { useCustomInsightsCatalog } from '@/modules/analytics/hooks/useCustomInsightsCatalog';
import { useExportDownload } from '@/modules/analytics/hooks/useExportDownload';
import { useMetricExportEntry } from '@/modules/analytics/hooks/useMetricExportEntry';
import { useReport } from '@/modules/analytics/hooks/useReport';
import type { MetricExportEntry } from '@/modules/analytics/types';
import { buildReportJsonPayload } from '@/modules/analytics/utils/buildReportExport';
import { downloadMetricsExport } from '@/modules/analytics/utils/downloadMetricsExport';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { getMetricDisplayName } from '@/modules/analytics/utils/metricDraft';
import type { ReportMetric } from '@/shared/types/reports';
import { PageName } from '@/shared/types/segment';

const NO_METRICS: ReportMetric[] = [];

type CollectedEntry = { entry: MetricExportEntry; isSettled: boolean };

type CollectorProps = {
  metric: ReportMetric;
  index: number;
  name: string;
  onChange: (index: number, collected: CollectedEntry) => void;
};

const MetricEntryCollector = ({ metric, index, name, onChange }: CollectorProps) => {
  const { entry, isSettled } = useMetricExportEntry(metric, index, name);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    onChange(index, { entry, isSettled });
  }, [onChange, index, entry, isSettled]);

  return null;
};

type Props = { reportId: string; format: ExportFormat; onDone: () => void };

export const ReportExportJob = ({ reportId, format, onDone }: Props) => {
  const { t } = useTranslation();
  const { report, status } = useReport(reportId);
  const { isLoading: isCatalogLoading } = useCustomInsightsCatalog();
  const { download } = useExportDownload();
  const [collected, setCollected] = useState<Record<number, CollectedEntry>>({});
  const doneRef = useRef(false);

  const handleChange = useRef((index: number, next: CollectedEntry) =>
    setCollected((prev) => ({ ...prev, [index]: next }))
  ).current;

  const metrics = report?.metrics ?? NO_METRICS;
  const allSettled = !!report && metrics.every((_, i) => collected[i]?.isSettled);

  useEffect(() => {
    if (doneRef.current) {
      return;
    }
    if (status === 'error') {
      doneRef.current = true;
      onDone();
      return;
    }
    if (!report || !allSettled) {
      return;
    }
    doneRef.current = true;
    const entries = metrics
      .map((_, i) => collected[i]?.entry)
      .filter((entry): entry is MetricExportEntry => !!entry?.hasData);
    if (entries.length === 0) {
      toast.info(t('analytics.reports_export_empty'));
    } else {
      download(() =>
        downloadMetricsExport(format, {
          entries,
          filename: buildExportFilename(report.name),
          title: 'reports',
          page: PageName.ANALYTICS_CUSTOM_REPORT_DETAIL,
          buildJson: () => buildReportJsonPayload(report.name, entries),
        })
      );
    }
    onDone();
  }, [status, report, allSettled, metrics, collected, format, onDone, t, download]);

  if (!report || isCatalogLoading) {
    return null;
  }
  return (
    <>
      {metrics.map((metric, i) => (
        <MetricEntryCollector
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          metric={metric}
          index={i}
          name={getMetricDisplayName(metric, i, t)}
          onChange={handleChange}
        />
      ))}
    </>
  );
};
